import type { StatusBarProgress } from '@rio-cloud/rio-uikit/components/statusBar/StatusBar.types';
import StatusBar from '@rio-cloud/rio-uikit/StatusBar';
import Tooltip from '@rio-cloud/rio-uikit/Tooltip';
import eq from 'lodash/fp/eq';
import gt from 'lodash/fp/gt';
import isNil from 'lodash/fp/isNil';
import isUndefined from 'lodash/fp/isUndefined';
import max from 'lodash/fp/max';
import min from 'lodash/fp/min';
import round from 'lodash/fp/round';
import { FormattedMessage } from 'react-intl';
import { Unit } from '../../common/constants';
import { getIntlFormattedCurrency } from '../../common/utils';
import { useAppSelector } from '../../configuration/setup/hooks';
import { selectIsEV } from '../../store/vehicleProfile/vehicleProfileSelectors';

interface TourSummaryPanelBodyProps {
    bodyDepTime?: string;
    bodyDepAddress?: string;
    bodyArrTime?: string;
    bodyArrAddress?: string;
    totalConsumption?: number;
    vehicleCost?: number;
    energyCost?: number;
    tollCostMin?: number;
    tollCostMax?: number;
    totalCostMin?: number;
    totalCostMax?: number;
    currency?: string;
}

export const TourSummaryPanelBody = ({
    bodyDepTime,
    bodyDepAddress,
    bodyArrTime,
    bodyArrAddress,
    totalConsumption,
    vehicleCost,
    energyCost,
    tollCostMin,
    tollCostMax,
    totalCostMin,
    totalCostMax,
    currency,
}: TourSummaryPanelBodyProps) => {
    const isEV = useAppSelector(selectIsEV).value;

    const totalCost = totalCostMax && totalCostMin && (totalCostMax + totalCostMin) / 2;
    const tollCost = (tollCostMax && tollCostMin && (tollCostMax + tollCostMin) / 2) || 0;
    const vehicleCostPct = vehicleCost && totalCost ? Number(round((vehicleCost * 100) / totalCost).toFixed(2)) : 0;
    const energyCostPct = energyCost && totalCost ? Number(round((energyCost * 100) / totalCost).toFixed(2)) : 0;
    const tollCostPct = tollCost && totalCost ? Number(round((tollCost * 100) / totalCost).toFixed(2)) : 0;

    const getProgressBarColor = (value: number): string | undefined => {
        const costsPctList = [vehicleCostPct, energyCostPct, tollCostPct];
        const maxValue = max(costsPctList);
        const minValue = min(costsPctList);

        if (eq(value, maxValue)) {
            return 'bg-coldplay-kashmir';
        }
        if (eq(value, minValue)) {
            return 'bg-info';
        }
        return 'bg-coldplay-fountain';
    };

    const displayCostEstimate = (minValue: number | undefined, maxValue: number | undefined) => {
        if (isUndefined(minValue) || isUndefined(maxValue)) {
            return '-';
        }

        if (eq(minValue, maxValue)) {
            return getIntlFormattedCurrency(minValue);
        }
        return `${getIntlFormattedCurrency(minValue)} - ${getIntlFormattedCurrency(maxValue)}`;
    };

    const generateStatusBarProgress = (): StatusBarProgress[] => {
        const items = [];

        if (vehicleCostPct > 0) {
            items.push({
                percentage: vehicleCostPct,
                color: getProgressBarColor(vehicleCostPct),
                tooltipPosition: Tooltip.BOTTOM,
                tooltip: (
                    <span>
                        <FormattedMessage id='intl-msg:smartRoute.vehicle.vehicleCosts' />
                        {`: ${vehicleCost} ${currency}`}
                    </span>
                ),
            });
        }

        if (energyCostPct > 0) {
            items.push({
                percentage: energyCostPct,
                color: getProgressBarColor(energyCostPct),
                tooltipPosition: Tooltip.BOTTOM,
                tooltip: (
                    <span>
                        <FormattedMessage id='intl-msg:smartRoute.vehicle.energyCosts' />
                        {`: ${energyCost} ${currency}`}
                    </span>
                ),
            });
        }

        if (tollCostPct > 0) {
            items.push({
                percentage: tollCostPct,
                color: getProgressBarColor(tollCostPct),
                tooltipPosition: 'bottom',
                tooltip: (
                    <span>
                        <FormattedMessage id='intl-msg:smartRoute.vehicle.tollCosts' />
                        {': '}&#126;{`${tollCost} ${currency}`}
                    </span>
                ),
            });
        }

        return items;
    };

    return (
        <div>
            <div data-testid='tour-from'>
                <div className='text-normal text-size-12 text-color-dark'>From</div>
                <div className='text-medium'>{bodyDepTime}</div>
                <div className='text-size-h6'>{bodyDepAddress}</div>
            </div>

            <div className='margin-top-20' data-testid='tour-to'>
                <div className='text-normal text-size-12 text-color-dark'>To</div>
                <div className='text-medium'>{bodyArrTime}</div>
                <div className='text-size-h6'>{bodyArrAddress}</div>
            </div>

            {!isNil(totalConsumption) && (
                <div className='margin-top-25 display-grid grid-cols-2 gap-15' data-testid='tour-details'>
                    {isEV && !isNil(totalConsumption) && (
                        <div>
                            <div className='text-normal text-size-12 text-color-dark'>
                                <FormattedMessage id='intl-msg:smartRoute.tour.totalConsumption' />
                            </div>
                            <div className='text-medium'>
                                {totalConsumption ? `${Math.round(totalConsumption)} ${Unit.KWH}` : '-'}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {!isNil(totalCost) && gt(totalCost, 0) && (
                <div className='margin-top-25 display-grid grid-cols-2 gap-15' data-testid='tour-cost-estimates'>
                    <div>
                        <div className='text-normal text-size-12 text-color-dark'>
                            <FormattedMessage id='intl-msg:smartRoute.tour.estimatedTollCost' />
                        </div>
                        <div className='text-medium'>{displayCostEstimate(tollCostMin ?? 0, tollCostMax ?? 0)}</div>
                    </div>
                    <div>
                        <div className='text-normal text-size-12 text-color-dark'>
                            <FormattedMessage id='intl-msg:smartRoute.tour.estimatedTotalCost' />
                        </div>
                        <div className='text-medium'>{displayCostEstimate(totalCostMin, totalCostMax)}</div>
                    </div>
                </div>
            )}

            {!isNil(totalCost) && gt(totalCost, 0) && (
                <div className='margin-top-15' data-testid='tour-costs'>
                    {/* <Headline
                        headline={<FormattedMessage id="intl-msg:smartRoute.tour.costEstimation" />}
                        description={totalCost ? `${getIntlFormattedCurrency(totalCost)}` : '-'}
                    /> */}
                    <StatusBar size='large' progress={generateStatusBarProgress()} />
                </div>
            )}
        </div>
    );
};
